import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MapComponent from '../components/Map/MapComponent';
import ServiceDiscoveryControls from '../components/ServiceProvider/ServiceDiscoveryControls';
import { useServiceDiscovery } from '../hooks/useServiceDiscovery';
import { isRenderableServicePosition } from '../utils/serviceDiscovery';

const MapPage: React.FC = () => {
  const location = useLocation();
  const isAuthenticatedRoute = location.pathname.startsWith('/app');

  const {
    services,
    filteredServices,
    uniqueCities,
    loading,
    error,
    query,
    city,
    sortBy,
    issueType,
    smartSelection,
    hourlyRateMax,
    includeUnpriced,
    searchState,
    setSortFilter,
    setCityFilter,
    setIssueTypeFilter,
    setHourlyRateMaxFilter,
    setIncludeUnpricedFilter,
    setQueryFromSearch,
    clearHourlyRateFilter,
    resetFilters,
    searchParams,
  } = useServiceDiscovery();

  const params = searchParams.toString();
  const mapUrl = `${isAuthenticatedRoute ? '/app/discover' : '/map'}${params ? `?${params}` : ''}`;
  const listUrl = `${isAuthenticatedRoute ? '/app/list' : '/list'}${params ? `?${params}` : ''}`;
  const fitContextKey = `${query.trim().toLowerCase()}|${city.trim().toLowerCase()}|${
    issueType || ''
  }|${smartSelection?.id || ''}`;
  const renderableResultsCount = useMemo(
    () =>
      filteredServices.filter((service) => isRenderableServicePosition(service.position)).length,
    [filteredServices]
  );
  const mapEmptyState = useMemo(() => {
    if (loading || error) return undefined;

    if (searchState.totalCount === 0) {
      return {
        title: 'Brak serwisów na mapie',
        message: 'Gdy pojawią się aktywne profile serwisowe z lokalizacją, zobaczysz je tutaj.',
      };
    }

    if (renderableResultsCount === 0) {
      if (searchState.isHourlyRateNoResults) {
        return {
          title: 'Nie znaleźliśmy serwisów z taką stawką.',
          message: 'Zwiększ limit albo uwzględnij serwisy bez podanej stawki.',
          actionLabel: 'Wyczyść stawkę',
          onAction: clearHourlyRateFilter,
        };
      }

      if (searchState.isSmartNoResults) {
        return {
          title: 'Nie znaleźliśmy specjalisty dla tej sprawy.',
          message: 'Wybierz szerszy obszar, zmień filtry albo zgłoś brakującą usługę.',
          actionLabel: 'Wyczyść filtry',
          onAction: resetFilters,
        };
      }

      return {
        title: 'Brak wyników na mapie',
        message: searchState.hasActiveFilters
          ? 'Zmień frazę lub miasto, aby wrócić do dostępnych serwisów.'
          : 'Nie znaleziono serwisów z poprawną lokalizacją.',
        actionLabel: searchState.hasActiveFilters ? 'Wyczyść filtry' : undefined,
        onAction: searchState.hasActiveFilters ? resetFilters : undefined,
      };
    }

    return undefined;
  }, [
    error,
    loading,
    renderableResultsCount,
    clearHourlyRateFilter,
    resetFilters,
    searchState.hasActiveFilters,
    searchState.isHourlyRateNoResults,
    searchState.isSmartNoResults,
    searchState.totalCount,
  ]);

  return (
    <div className="rs-page-gutter flex h-full min-h-0 flex-col overflow-hidden pb-0 pt-1 md:pt-2.5">
      <div className="mx-auto flex w-full max-w-6xl flex-1 min-h-0 flex-col gap-1.5 overflow-hidden md:gap-2.5">
        <ServiceDiscoveryControls
          activeView="map"
          mapUrl={mapUrl}
          listUrl={listUrl}
          resultsCount={filteredServices.length}
          resultsCountLoading={loading}
          query={query}
          city={city}
          sortBy={sortBy}
          issueType={issueType}
          smartSelection={smartSelection}
          hourlyRateMax={hourlyRateMax}
          includeUnpriced={includeUnpriced}
          cities={uniqueCities}
          services={services}
          disabled={loading}
          onSearchChange={setQueryFromSearch}
          onCityChange={setCityFilter}
          onSortChange={setSortFilter}
          onIssueTypeChange={setIssueTypeFilter}
          onHourlyRateMaxChange={setHourlyRateMaxFilter}
          onIncludeUnpricedChange={setIncludeUnpricedFilter}
          onResetFilters={resetFilters}
        />

        {/* This container owns leftover height below controls; map relies on flex-1 + min-h-0. */}
        <div className="flex-1 min-h-0 overflow-hidden pb-[max(env(safe-area-inset-bottom),0px)]">
          <MapComponent
            markersData={filteredServices}
            loading={loading}
            error={error}
            searchQuery={query}
            smartSelection={smartSelection}
            issueType={issueType}
            fitContextKey={fitContextKey}
            resultsOverlayLabel="Dopasuj mapę"
            emptyState={mapEmptyState}
          />
        </div>
      </div>
    </div>
  );
};

export default MapPage;
